/* eslint-disable no-await-in-loop */
import allPromisesWithRetries from '@/helpers/allPromisesWithRetries'
import { areAllShipmentsAssigned } from '@/helpers/areAllShipmentsAssigned'

import { log, error } from '@/services/Log'
import { BCCheckoutType } from '@/types/BigCommerce/BCCheckout'
import type { StandardCart, StandardOrder } from '@/types/ShopFront/CheckoutStandards'
// Recovers the original oder consignments for a new cart generated after a bigcAPI interaction.
export const recoverShippingOptions = async ({
  order, newCart, newOrder: newOrderInput = null,
}: {
  order: StandardOrder,
  newOrder?: StandardOrder | null
  newCart: StandardCart,
}): Promise<{ success: boolean, newOrder: StandardOrder }> => {
  // the shipment option ID changes in bigcommerce so we need to update the consignment
  log('recoverShippingOptions START', { order, newCart, newOrderInput })
  const [
    { default: getOrder },
    { standardizeOrderFromResponse },
    { isValidAddress },
    { default: Axios },
    { recoverAvailableShippingOptions },
  ] = await allPromisesWithRetries(() => [
    import('@/services/FramedCheckout/orderServices/getOrder'),
    import('@/services/Standardizers/standardizeOrderFromResponse'),
    import('@/helpers/addressHelpers'),
    import('axios'),
    import('./recoverAvailableShippingOptions'),
  ])
  let newOrder = newOrderInput ? { ...newOrderInput } : await getOrder({ cart: newCart })
  for (let i = 0; i < newOrder.shipments.length; i += 1) {
    // Queueing shippingOptionId assignments due to its interference in other shipments
    const consignment = newOrder.shipments[i]
    const {
      id: shipmentId,
      selectedShippingOption: currentShippingOption,
      shippingAddress,
    } = consignment
    let { availableShippingOptions } = consignment
    if (!availableShippingOptions.length && isValidAddress(shippingAddress)) {
      const recoveredOptions = await recoverAvailableShippingOptions({
        order: newOrder, shipmentId,
      })
      if (recoveredOptions?.success) {
        const shipment = recoveredOptions?.shipment
        availableShippingOptions = shipment?.availableShippingOptions
        newOrder = recoveredOptions?.newOrder
      } else {
        error('recoverShippingOptions FAILED recoverAvailableShippingOptions FAILED')
      }
    }
    const expectedShippingOption = order?.shipments?.find(
      ({ id }) => id === shipmentId,
    )?.selectedShippingOption
    // Check if the shippingOption description is different.
    // Note on shippingOptions:
    // {id,type,description} =
    // (id = volatile, type = constant across options, We are forced to use the description !)
    if (
      expectedShippingOption
      && expectedShippingOption?.description !== currentShippingOption?.description
    ) {
      const shippingOptionId = (
        availableShippingOptions?.find(({ description }) => (
          description === expectedShippingOption.description
        ))?.id
        || availableShippingOptions?.[0]?.id
      )
      if (shippingOptionId) {
        // Updates an existing consignment - https://developer.bigcommerce.com/api-reference/storefront/checkouts/checkout-consignments/checkoutsconsignmentsbycheckoutidandconsignmentidput
        // line item IDs or the shipping option ID can be updated using this endpoint.
        const { status, data } = (await Axios.put<BCCheckoutType>(`/api/storefront/checkouts/${newOrder?.cart?.id}/consignments/${shipmentId}?include=consignments.availableShippingOptions`, JSON.stringify({ shippingOptionId })))
        if (status !== 200) {
          return { success: false, newOrder: order }
        } if (data) {
          // update newOrder with the response data.
          // other consignments' shipping options IDs change on every request
          newOrder = await standardizeOrderFromResponse({ order, bigcResponse: data })
        }
      }
    } else if (!currentShippingOption && expectedShippingOption) {
      newOrder = await getOrder({ cart: newCart })
      log('recoverShippingOptions END', { success: false, newOrder, areAllShipmentsAssigned: areAllShipmentsAssigned(newOrder) })
      return { success: false, newOrder }
    }
  }
  log('recoverShippingOptions END', { success: true, newOrder, areAllShipmentsAssigned: areAllShipmentsAssigned(newOrder) })
  return { success: true, newOrder }
}

export default recoverShippingOptions
